import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CreateBeaconFundRequestValidation, useCreateBeaconRequest } from "@/lib/api/fund";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getUserDbId } from "@/lib/models/auth";
import { useStytchMember } from "@stytch/react/b2b";
import { BeaconFundRequestForm } from "./components/BeaconFundRequestForm";

type FormData = z.infer<typeof CreateBeaconFundRequestValidation>;

export function CreateBeaconFundRequestModal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const createRequest = useCreateBeaconRequest();
  const { member } = useStytchMember();
  const userId = getUserDbId(member);
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(CreateBeaconFundRequestValidation),
    defaultValues: {
      survey: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        matchingGiftAmount: "",
        recipientOrganization: "",
        recipientOrganizationMailingAddress: "",
        someOrAllMatchingConfirmation: "",
        organizationPurpose: "",
      },
      has_been_granted: false,
      has_been_dispersed: false,
      amount_granted: undefined,
      customer_id: null,
      employee_id: null,
      customer_requested: null,
    },
  });

  const isOpen = searchParams.get("create_request") === "true";

  const handleClose = () => {
    searchParams.delete("create_request");
    setSearchParams(searchParams);
    form.reset();
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      const parsedResult = CreateBeaconFundRequestValidation.safeParse(data);
      if (!parsedResult.success) {
        toast({
          title: "Invalid request",
          description: "",
        });
        return;
      }
      
      const result = await createRequest(parsedResult.data);
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Beacon fund request created successfully",
        });
        handleClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to create request",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Beacon Fund Request</DialogTitle>
          <DialogDescription>
            Create a new beacon fund matching request with all survey details
          </DialogDescription>
        </DialogHeader>

        <BeaconFundRequestForm
          form={form}
          onSubmit={onSubmit}
          isLoading={isLoading}
          onCancel={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
}
